// Карта на экране прогресса: выбрать цель и предъявить знание зачётом.
//
// Правила 2 и 3 `docs/11-entry.md`. Один список навыков на два действия — они
// об одном и том же выборе: «вот эта тема». Разводить их по двум экранам
// значило бы дважды спросить одно.
//
// Кнопки «я это знаю» здесь нет и не будет: инвариант 3 говорит, что
// состояние ученика считается из его действий, а не из его слов. Есть
// «Сдать зачёт» — три задачи наибольшей ступени.
//
// Под выбранным навыком стоит не только состояние, но и откуда оно. Зачёт
// 3 из 3 зачитывает всё замыкание предпосылок, поэтому «уже зачтён»
// достаётся навыкам, которых ученик не открывал, а кнопка зачёта у них
// гаснет. Проба 11 сентября 2026 остановилась на этом первым же вопросом:
// «как цель может быть достигнута, если задач по ней не было». Числа и имя
// того зачёта отвечают; слово «зачтён» само за себя не говорит.

import { useState } from "react";
import { api, useMutation, useQuery, type AimRoute, type ProgressSnapshot } from "../api";
import { creditLines } from "../lib/credit";
import { reasonOf } from "../lib/errors";
import { formatDay, plural } from "../lib/dates";
import { EvidenceTag } from "./EvidenceTag";
import { SkillPicker } from "./SkillPicker";

/** Оценка в днях: планировщик открывает по одной теме в день. */
function daysFor(remaining: number): string {
  if (remaining >= 14) {
    const weeks = Math.round(remaining / 7);
    return `≈ ${weeks} ${plural(weeks, "неделя", "недели", "недель")}`;
  }
  return `≈ ${remaining} ${plural(remaining, "день", "дня", "дней")}`;
}

export function AimCard({
  learnerKey,
  catalog,
  aim,
  sections,
  openSkills,
  picked,
  onPick,
  onChanged,
  onTestOut,
  onPractice,
  onDrill,
}: {
  learnerKey: string;
  catalog: ProgressSnapshot["catalog"];
  aim: AimRoute | null;
  sections: ProgressSnapshot["sections"];
  /** Открытые навыки: владение и свидетельство под ним по выбранной теме. */
  openSkills: ProgressSnapshot["openSkills"];
  /** Выбранная тема. Живёт на экране: её выбирают и списком открытых навыков. */
  picked: string | null;
  onPick: (skillId: string | null) => void;
  /** Снимок устарел: цель или состояние изменились. */
  onChanged: () => void;
  /** Зачёт начат — экран уходит в задачи. */
  onTestOut: () => void;
  /** Тренажёр по выбранной теме. Работает и на закрытой — он ничего не двигает. */
  onDrill: (skillId: string) => void;
  /**
   * Задачи по теме добавлены — экран уходит прямо в первую из них, как у
   * «Своей темы» на главной. До 25 сентября 2026 он уходил на «Сегодня», где
   * «Продолжить» вело в незакрытый хвост набора, а добавленные три задачи
   * стояли за ним в конце: ученик выбирал тему и получал другую.
   */
  onPractice: (firstItemKey: string) => void;
}) {
  const setAim = useMutation(api.goal.setAim);
  const clearAim = useMutation(api.goal.clearAim);
  const startTestOut = useMutation(api.testout.start);
  const practice = useMutation(api.today.practice);
  // Узкий срез (инвариант 4): один навык, зачёты ученика и одна строка
  // диагностики. Пока навык не выбран — "skip", то есть ни одного вызова.
  const credit = useQuery(
    api.testout.creditOf,
    picked === null ? "skip" : { learnerKey, skillId: picked },
  );
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const named = new Map(sections.map((s) => [s.id, s.name]));
  const items = catalog.map((c) => ({
    id: c.skillId,
    name: c.name,
    hint: named.get(c.section) ?? c.section,
  }));
  const chosen = picked === null ? null : (catalog.find((c) => c.skillId === picked) ?? null);
  const lines = credit === undefined ? null : creditLines(credit);
  // Строка открытого навыка: владение и чем оно подпёрто. У закрытого её нет,
  // и это не пропуск — у него нет и владения, о котором стоило бы говорить.
  const level = picked === null ? null : (openSkills.find((s) => s.skillId === picked) ?? null);

  const run = <T,>(action: Promise<T>, then: (result: T) => void): void => {
    setBusy(true);
    setFailed(null);
    void action
      .then((result) => {
        setBusy(false);
        then(result);
      })
      .catch((e: unknown) => {
        setBusy(false);
        setFailed(reasonOf(e));
      });
  };

  return (
    <div className="card">
      <div className="card__label">Карта: цель и зачёт</div>

      {aim === null ? (
        <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
          Цели нет — система открывает темы по своему порядку. Выберите любую тему
          карты, и навыки её маршрута пойдут в наборе первыми.
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          <div className="move__head">
            <span className="move__name">{aim.name}</span>
            <span className="move__value num">
              {aim.done} / {aim.total}
            </span>
          </div>
          <div className="bar">
            <div className="bar__base" style={{ width: `${(aim.done / aim.total) * 100}%` }} />
          </div>
          <div className="faint" style={{ marginTop: 6 }}>
            {aim.reached ? (
              "цель достигнута — можно взять следующую"
            ) : (
              <>
                осталось <span className="num">{aim.remaining.length}</span>{" "}
                {plural(aim.remaining.length, "навык", "навыка", "навыков")} ·{" "}
                {daysFor(aim.remaining.length)}
              </>
            )}
          </div>
        </div>
      )}

      <hr className="hr" />

      <SkillPicker items={items} value={picked} onPick={onPick} placeholder="найти тему" />

      {chosen === null ? null : (
        <>
          <div className="faint" style={{ marginTop: 10 }}>
            {chosen.credited
              ? "уже зачтён"
              : chosen.open
                ? "открыт, но не доведён"
                : "закрыт"}
            {chosen.covered ? "" : " · задач в пуле нет"}
          </div>
          {credit === undefined || lines === null ? null : (
            <>
              {/* Обе строки независимы: свой зачёт можно провалить, а навык
                  всё равно окажется зачтён чужим замыканием — и тогда нужны
                  обе. Правило — `creditLines`, оно под тестами. */}
              {!lines.own || credit.own === null ? null : (
                <div className="faint" style={{ marginTop: 6 }}>
                  зачёт {formatDay(credit.own.date)}:{" "}
                  <span className="num">{credit.own.correct}</span> из{" "}
                  <span className="num">{credit.own.total}</span>
                </div>
              )}
              {!lines.via || credit.via === null ? null : (
                // Единственное, что ученик не может восстановить сам: навык
                // зачтён не им, а тем, что стоит выше.
                <div className="faint" style={{ marginTop: 6 }}>
                  стоит под зачётом «{credit.via.name}», сданным{" "}
                  {formatDay(credit.via.date)}
                </div>
              )}
            </>
          )}
          {level === null ? null : (
            <div className="row" style={{ gap: 6, marginTop: 8, alignItems: "center" }}>
              <span className="faint">
                владение <span className="num">{level.level}</span>
              </span>
              {/* Те же слова, что под навыком в списке выше: про один навык
                  экран обязан говорить одинаково в обоих местах. */}
              <EvidenceTag skill={level} />
              {level.due ? <span className="tag">пора повторить</span> : null}
            </div>
          )}
          <div className="row row--actions" style={{ marginTop: 12 }}>
            {aim?.skillId === chosen.skillId ? (
              <button
                className="btn"
                disabled={busy}
                onClick={() => run(clearAim({ learnerKey }), onChanged)}
              >
                Снять цель
              </button>
            ) : (
              <button
                className="btn btn--primary"
                disabled={busy}
                onClick={() => run(setAim({ learnerKey, skillId: chosen.skillId }), onChanged)}
              >
                Сделать целью
              </button>
            )}
            <button
              className="btn"
              // Зачитывать нечего и нечем — две разные причины, обе видны
              // строкой выше. Кнопка гасится до нажатия, а не после отказа.
              disabled={busy || chosen.credited || !chosen.covered}
              onClick={() =>
                run(startTestOut({ learnerKey, skillId: chosen.skillId }), onTestOut)
              }
            >
              Сдать зачёт
            </button>
            {/*
              Открытую тему взять в работу можно всегда, зачтённую — тоже:
              именно зачтённую и хотят порешать чаще прочих, потому что
              замыкание чужого зачёта раздаёт 0,85 навыкам, которых ученик не
              видел. Закрытую — нельзя, и это не осторожность интерфейса:
              первый же ответ открыл бы её в обход предпосылок. Для закрытой
              есть зачёт, он рядом.
            */}
            <button
              className="btn"
              disabled={busy || !chosen.covered || !chosen.open}
              onClick={() =>
                run(practice({ learnerKey, skillId: chosen.skillId }), (r) =>
                  onPractice(r.firstItemKey),
                )
              }
            >
              Решать
            </button>
            {/*
              Единственная кнопка, которой всё равно, открыта тема или нет:
              тренажёр не двигает ни владения, ни открытости и в набор дня не
              идёт. Гаснет он только там, где задач нет физически.
            */}
            <button
              className="btn"
              disabled={busy || !chosen.covered}
              onClick={() => onDrill(chosen.skillId)}
            >
              Тренажёр
            </button>
          </div>
          <div className="faint" style={{ marginTop: 8 }}>
            Зачёт — три задачи посложнее, без подсказок. Три из трёх засчитывают
            тему вместе со всем, на чём она стоит; две из трёх открывают её;
            меньше — не меняют ничего. «Решать» — три задачи по уже открытой
            теме сверх набора дня, ступень по владению, сразу с первой из них;
            нажать можно сколько угодно раз. Закрытую тему добавка не открывает — для этого зачёт.
            Тренажёр идёт по любой теме, с разбором после каждой задачи, и не
            меняет ничего: ни набора дня, ни владения, ни открытости.
          </div>
        </>
      )}

      {failed === null ? null : (
        <div className="faint" style={{ marginTop: 10 }}>
          {failed}
        </div>
      )}
    </div>
  );
}
